import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { getMonthGrid, getWeekBars } from '../utils/calendarGrid';
import {
  ChevronLeft, ChevronRight, Plus, X, Loader2,
  Video, Clock, MapPin, Pencil, Trash2, Check, ExternalLink,
  CalendarDays, Settings,
} from 'lucide-react';
import GoogleConnectPrompt from './GoogleConnectPrompt';

function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}
function toLocalDatetime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function formatTime(dtStr) {
  if (!dtStr) return '';
  return new Date(dtStr).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}
function formatEventTime(ev) {
  if (ev.start?.date) return '종일';
  const s = ev.start?.dateTime, e = ev.end?.dateTime;
  if (!s) return '';
  return formatTime(s) + (e ? ' ~ ' + formatTime(e) : '');
}

// 배경색이 밝으면 black, 어두우면 white 반환
function autoTextColor(bgHex) {
  if (!bgHex || bgHex.length < 7) return '#ffffff';
  const r = parseInt(bgHex.slice(1, 3), 16);
  const g = parseInt(bgHex.slice(3, 5), 16);
  const b = parseInt(bgHex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 145 ? '#1a1a1a' : '#ffffff';
}

// UTC 기준 날짜 빼기 (timezone 영향 없음)
function subtractDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

// Google 이벤트를 bar용 형태로 변환
// Google의 종일 이벤트 end.date는 exclusive이므로 -1일
function evToBar(ev) {
  const startDate = ev.start?.date || ev.start?.dateTime?.slice(0, 10) || '';
  let endDate = ev.end?.date || ev.end?.dateTime?.slice(0, 10) || startDate;
  if (ev.start?.date && endDate > startDate) {
    endDate = subtractDay(endDate);
  }
  return { ...ev, startDate, endDate };
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const PRESET_COLORS = [
  { bg: '#7986CB', fg: '#ffffff' }, { bg: '#33B679', fg: '#ffffff' },
  { bg: '#8E24AA', fg: '#ffffff' }, { bg: '#E67C73', fg: '#ffffff' },
  { bg: '#F6BF26', fg: '#000000' }, { bg: '#F4511E', fg: '#ffffff' },
  { bg: '#039BE5', fg: '#ffffff' }, { bg: '#616161', fg: '#ffffff' },
  { bg: '#3F51B5', fg: '#ffffff' }, { bg: '#0B8043', fg: '#ffffff' },
  { bg: '#D50000', fg: '#ffffff' }, { bg: '#F09300', fg: '#ffffff' },
];

const EMPTY_FORM = {
  id: null, calendarId: 'primary', originalCalendarId: null,
  title: '', startDate: '', endDate: '', startTime: '09:00', endTime: '10:00',
  allDay: true, description: '', location: '', meetLink: false, hangoutLink: '',
};
const EMPTY_CAT_FORM = { name: '', backgroundColor: '#039BE5', foregroundColor: '#ffffff' };

export default function GoogleCalendarView() {
  const todayStr = koreaToday();
  const [year, setYear] = useState(() => +todayStr.split('-')[0]);
  const [month, setMonth] = useState(() => +todayStr.split('-')[1] - 1);

  const [connected, setConnected] = useState(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [hiddenCals, setHiddenCals] = useState({});
  const [events, setEvents] = useState([]);
  const [hoveredCal, setHoveredCal] = useState(null);

  const [selectedDay, setSelectedDay] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showCatMgr, setShowCatMgr] = useState(false);
  const [catForm, setCatForm] = useState(EMPTY_CAT_FORM);
  const [editingCal, setEditingCal] = useState(null);
  const [catSaving, setCatSaving] = useState(false);
  const [catDeleting, setCatDeleting] = useState(null);

  useEffect(() => {
    api.get('/google/status')
      .then(r => { setConnected(r.data.connected); setNeedsReconnect(r.data.needsReconnect || false); })
      .catch(() => setConnected(false));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const timeMin = new Date(year, month, 1).toISOString();
      const timeMax = new Date(year, month + 1, 1).toISOString();
      const r = await api.get('/google/calendar/events', { params: { timeMin, timeMax } });
      setCalendars(r.data.calendars || []);
      setEvents(r.data.events || []);
    } catch (e) {
      if (e.response?.status === 401) setConnected(false);
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { if (connected) loadAll(); }, [loadAll, connected]);

  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < grid.length; i += 7) rows.push(grid.slice(i, i + 7));
    return rows;
  }, [grid]);

  const visibleEvents = useMemo(
    () => events.filter(ev => !hiddenCals[ev._calendarId]).map(evToBar),
    [events, hiddenCals]
  );
  const timedByDate = useMemo(() => {
    const m = {};
    visibleEvents.forEach(ev => {
      if (!ev.start?.dateTime) return;
      const d = ev.startDate;
      if (!m[d]) m[d] = [];
      m[d].push(ev);
    });
    return m;
  }, [visibleEvents]);
  const allDayEvents = useMemo(() => visibleEvents.filter(ev => ev.start?.date), [visibleEvents]);
  const weekBarsMap = useMemo(
    () => weeks.map(w => getWeekBars(w, allDayEvents, 'schedule')),
    [weeks, allDayEvents]
  );

  function prevMonth() { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); }

  function openCreate(dateStr = todayStr) {
    const defaultCal = calendars.find(c => c.primary)?.id || calendars[0]?.id || 'primary';
    setForm({ ...EMPTY_FORM, calendarId: defaultCal, startDate: dateStr, endDate: dateStr });
    setIsEditing(false);
    setDetailEvent(null);
    setShowForm(true);
  }
  function openEdit(ev) {
    const allDay = !!ev.start?.date;
    setForm({
      id: ev.id,
      calendarId: ev._calendarId || 'primary',
      originalCalendarId: ev._calendarId || 'primary',
      title: ev.summary || '',
      startDate: ev.start?.date || ev.start?.dateTime?.slice(0, 10) || '',
      endDate: allDay
        ? (ev.endDate || ev.start?.date || '')
        : (ev.end?.dateTime?.slice(0, 10) || ev.start?.dateTime?.slice(0, 10) || ''),
      startTime: ev.start?.dateTime ? toLocalDatetime(ev.start.dateTime).slice(11) : '09:00',
      endTime: ev.end?.dateTime ? toLocalDatetime(ev.end.dateTime).slice(11) : '10:00',
      allDay,
      description: ev.description || '',
      location: ev.location || '',
      meetLink: false,
      hangoutLink: ev.hangoutLink || '',
    });
    setIsEditing(true);
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.startDate) return;
    setSaving(true);
    try {
      const tz = 'Asia/Seoul';
      const event = { summary: form.title };
      if (form.description) event.description = form.description;
      if (form.location) event.location = form.location;
      if (form.allDay) {
        event.start = { date: form.startDate };
        const [y, m, d] = (form.endDate || form.startDate).split('-').map(Number);
        event.end = { date: new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10) };
      } else {
        event.start = { dateTime: `${form.startDate}T${form.startTime}:00`, timeZone: tz };
        event.end = { dateTime: `${form.endDate || form.startDate}T${form.endTime}:00`, timeZone: tz };
      }
      if (form.meetLink && !form.allDay) {
        event.conferenceData = { createRequest: { requestId: `meet-${Date.now()}`, conferenceSolutionKey: { type: 'hangoutsMeet' } } };
      }
      if (isEditing && form.id) {
        await api.put(`/google/calendar/events/${form.id}`, {
          calendarId: form.calendarId,
          originalCalendarId: form.originalCalendarId !== form.calendarId ? form.originalCalendarId : undefined,
          event,
          conferenceDataVersion: form.meetLink ? 1 : 0,
        });
      } else {
        await api.post('/google/calendar/events', { calendarId: form.calendarId, event, conferenceDataVersion: form.meetLink ? 1 : 0 });
      }
      setShowForm(false);
      setDetailEvent(null);
      setSelectedDay(null);
      loadAll();
    } catch (e) { alert('저장 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setSaving(false); }
  }

  async function handleDelete(ev) {
    if (!confirm('이 일정을 삭제하시겠습니까?')) return;
    setDeleting(true);
    try {
      await api.delete(`/google/calendar/events/${ev.id}`, { params: { calendarId: ev._calendarId || 'primary' } });
      setDetailEvent(null);
      setSelectedDay(null);
      loadAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setDeleting(false); }
  }

  function openCreateCal() { setEditingCal(null); setCatForm(EMPTY_CAT_FORM); }
  function openEditCal(cal) { setEditingCal(cal); setCatForm({ name: cal.summary, backgroundColor: cal.backgroundColor, foregroundColor: cal.foregroundColor || '#ffffff' }); }

  async function handleSaveCal() {
    if (!catForm.name.trim()) return;
    setCatSaving(true);
    try {
      if (editingCal) {
        await api.put(`/google/calendar/calendars/${encodeURIComponent(editingCal.id)}`, {
          summary: catForm.name !== editingCal.summary ? catForm.name : undefined,
          backgroundColor: catForm.backgroundColor,
          foregroundColor: catForm.foregroundColor,
        });
      } else {
        await api.post('/google/calendar/calendars', {
          summary: catForm.name,
          backgroundColor: catForm.backgroundColor,
          foregroundColor: catForm.foregroundColor,
        });
      }
      setEditingCal(null);
      setCatForm(EMPTY_CAT_FORM);
      loadAll();
    } catch (e) { alert('저장 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setCatSaving(false); }
  }

  async function handleDeleteCal(cal) {
    if (cal.primary) { alert('기본 캘린더는 삭제할 수 없습니다.'); return; }
    if (!confirm(`'${cal.summary}' 캘린더와 모든 일정을 삭제하시겠습니까?`)) return;
    setCatDeleting(cal.id);
    try {
      await api.delete(`/google/calendar/calendars/${encodeURIComponent(cal.id)}`);
      loadAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setCatDeleting(null); }
  }

  if (connected === false) return <GoogleConnectPrompt height="h-64" reconnect={needsReconnect} />;

  const dayStr = selectedDay ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(selectedDay) : null;
  const dayTimedEvents = dayStr ? (timedByDate[dayStr] || []) : [];
  const dayAllDayEvents = dayStr ? allDayEvents.filter(ev => ev.startDate <= dayStr && ev.endDate >= dayStr) : [];
  const dayEvents = [...dayAllDayEvents, ...dayTimedEvents];

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      {/* 헤더: 타이틀 | 월 네비게이션 (센터) | 액션 버튼 */}
      <div className="flex items-center justify-between mb-1 flex-shrink-0">
        {/* 왼쪽: 타이틀 */}
        <div className="flex items-center gap-2 flex-1">
          <CalendarDays size={20} className="text-brand flex-shrink-0" />
          <h1 className="text-xl font-bold text-white">캘린더</h1>
        </div>

        {/* 가운데: 월 네비게이션 */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={prevMonth} className="p-1.5 rounded hover:bg-gray-800 text-gray-400 transition-colors"><ChevronLeft size={15} /></button>
          <span className="text-sm font-bold text-white min-w-[96px] text-center">{year}년 {month + 1}월</span>
          <button onClick={nextMonth} className="p-1.5 rounded hover:bg-gray-800 text-gray-400 transition-colors"><ChevronRight size={15} /></button>
          <button
            onClick={() => { const t = koreaToday().split('-'); setYear(+t[0]); setMonth(+t[1] - 1); }}
            className="px-2 py-0.5 rounded border border-gray-700 text-xs text-gray-400 hover:bg-gray-800 transition-colors ml-1"
          >오늘</button>
        </div>

        {/* 오른쪽: 액션 버튼 */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <button
            onClick={() => setShowCatMgr(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-800 text-sm transition-colors"
          >
            <Settings size={13} /> 캘린더 관리
          </button>
          <button
            onClick={() => openCreate()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand/80"
          >
            <Plus size={13} /> 일정 추가
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="animate-spin text-gray-500" size={22} /></div>
      ) : (
        <div className="flex gap-3 flex-1 min-h-0">
          {/* 왼쪽 사이드바: 캘린더 목록 */}
          <div className="w-40 flex-shrink-0 flex flex-col gap-0.5">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5 px-1">내 캘린더</p>
            {calendars.map(cal => {
              const hidden = !!hiddenCals[cal.id];
              const isHovered = hoveredCal === cal.id;
              return (
                <button
                  key={cal.id}
                  onClick={() => setHiddenCals(h => ({ ...h, [cal.id]: !h[cal.id] }))}
                  onMouseEnter={() => setHoveredCal(cal.id)}
                  onMouseLeave={() => setHoveredCal(null)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-left w-full transition-colors"
                  style={{ backgroundColor: isHovered ? (cal.backgroundColor + '22') : 'transparent' }}
                >
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0 flex items-center justify-center border transition-all"
                    style={{ backgroundColor: hidden ? 'transparent' : cal.backgroundColor, borderColor: cal.backgroundColor }}
                  >
                    {!hidden && <Check size={8} color={autoTextColor(cal.backgroundColor)} strokeWidth={3} />}
                  </span>
                  <span className={`text-xs truncate transition-colors ${hidden ? 'text-gray-600' : isHovered ? 'text-white' : 'text-gray-300'}`}>
                    {cal.summary}
                  </span>
                </button>
              );
            })}
          </div>

          {/* 캘린더 그리드 */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="grid grid-cols-7 border-b border-gray-800 flex-shrink-0">
              {WEEKDAYS.map((d, i) => (
                <div key={d} className={`text-center py-2 text-xs font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'}`}>{d}</div>
              ))}
            </div>

            {(() => {
              const frVals = weeks.map((_, wi) => {
                const maxRow = weekBarsMap[wi].reduce((m, b) => Math.max(m, b.row), -1) + 1;
                return Math.max(72, 38 + maxRow * 22);
              });
              return (
                <div className="flex-1 min-h-0" style={{ display: 'grid', gridTemplateRows: frVals.map(v => `minmax(0, ${v}fr)`).join(' ') }}>
                  {weeks.map((weekCells, wi) => {
                    const bars = weekBarsMap[wi];
                    const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
                    return (
                      <div key={wi} className="border-b border-gray-800/50 relative overflow-hidden">
                        {/* 배경 셀 */}
                        <div className="absolute inset-0 grid grid-cols-7">
                          {weekCells.map((cell, di) => {
                            const isSelected = dayStr === cell?.dateStr;
                            return (
                              <div
                                key={di}
                                onClick={() => cell && setSelectedDay(isSelected ? null : new Date(cell.dateStr + 'T12:00:00'))}
                                className={`${di < 6 ? 'border-r border-gray-800/30' : ''} transition-colors cursor-pointer
                                  ${!cell?.isCurrentMonth ? 'bg-gray-950/30' : ''}
                                  ${isSelected ? 'bg-brand/10' : cell?.isCurrentMonth ? 'hover:bg-brand/5' : ''}`}
                              />
                            );
                          })}
                        </div>

                        {/* 날짜 숫자 */}
                        <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                          {weekCells.map((cell, di) => {
                            if (!cell) return <div key={di} />;
                            const isToday = cell.dateStr === todayStr;
                            const isSelected = dayStr === cell.dateStr;
                            return (
                              <div key={di} className="px-1 pt-1 pb-0.5">
                                <span className={`text-xs font-semibold inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${
                                  isToday ? 'bg-brand text-white'
                                    : isSelected ? 'text-brand'
                                    : !cell.isCurrentMonth ? 'text-gray-700'
                                    : di === 0 ? 'text-red-400'
                                    : di === 6 ? 'text-blue-400'
                                    : 'text-gray-300'
                                }`}>{cell.day}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* 종일 이벤트 바 */}
                        <div className="relative z-10 mt-0.5">
                          {Array.from({ length: maxRow }, (_, ri) => (
                            <div key={ri} className="grid grid-cols-7" style={{ height: '22px' }}>
                              {bars.filter(b => b.row === ri).map(b => {
                                const ev = b.schedule;
                                const bg = ev._calendarColor || '#039BE5';
                                const fg = autoTextColor(bg);
                                return (
                                  <div
                                    key={ev.id}
                                    onClick={e => { e.stopPropagation(); setDetailEvent(ev); }}
                                    className={`h-[19px] mx-[1px] flex items-center text-[10px] font-medium truncate cursor-pointer hover:brightness-110 transition-all select-none
                                      ${b.isStart && b.isEnd ? 'rounded-md' : b.isStart ? 'rounded-l-md' : b.isEnd ? 'rounded-r-md' : ''}`}
                                    style={{ gridColumn: `${b.startCol + 1} / span ${b.span}`, backgroundColor: bg, color: fg }}
                                  >
                                    <span className="ml-1.5 truncate">{ev.summary || '(제목 없음)'}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>

                        {/* 시간-지정 이벤트 */}
                        <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                          {weekCells.map((cell, di) => {
                            if (!cell) return <div key={di} />;
                            const tevs = timedByDate[cell.dateStr] || [];
                            if (!tevs.length) return <div key={di} />;
                            return (
                              <div key={di} className="px-0.5 pb-0.5 space-y-px pointer-events-auto">
                                {tevs.slice(0, 2).map(ev => {
                                  const bg = ev._calendarColor || '#039BE5';
                                  const fg = autoTextColor(bg);
                                  return (
                                    <div
                                      key={ev.id}
                                      onClick={e => { e.stopPropagation(); setDetailEvent(ev); }}
                                      className="text-[9px] truncate px-1 rounded leading-tight py-0.5 cursor-pointer hover:brightness-110 transition-all"
                                      style={{ backgroundColor: bg + '2e', color: bg }}
                                    >
                                      {formatTime(ev.start?.dateTime)} {ev.summary || '(제목 없음)'}
                                    </div>
                                  );
                                })}
                                {tevs.length > 2 && <div className="text-[8px] text-gray-500 pl-1">+{tevs.length - 2}개</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* 오른쪽: 날짜 상세 */}
          {selectedDay && !showForm && (
            <div className="w-52 flex-shrink-0 border border-gray-800 rounded-xl p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-200">
                  {selectedDay.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}
                </span>
                <button onClick={() => { setSelectedDay(null); setDetailEvent(null); }} className="p-1 rounded hover:bg-gray-800 text-gray-500"><X size={13} /></button>
              </div>
              {dayEvents.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-3">일정 없음</p>
              ) : dayEvents.map(ev => (
                <div
                  key={ev.id}
                  onClick={() => setDetailEvent(ev)}
                  className={`p-2 rounded-lg border cursor-pointer transition-all hover:bg-gray-800/40 ${detailEvent?.id === ev.id ? 'border-brand/40 bg-gray-800/30' : 'border-gray-800'}`}
                >
                  <div className="flex items-start gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: ev._calendarColor }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-200 truncate">{ev.summary || '(제목 없음)'}</p>
                      {formatEventTime(ev) && (
                        <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5"><Clock size={8} />{formatEventTime(ev)}</p>
                      )}
                      {ev.location && <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5 truncate"><MapPin size={8} />{ev.location}</p>}
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={() => openCreate(dayStr)}
                className="w-full mt-auto py-1.5 rounded-lg border border-gray-700 hover:bg-gray-800 text-xs text-gray-400 flex items-center justify-center gap-1 transition-colors"
              >
                <Plus size={11} /> 일정 추가
              </button>
            </div>
          )}
        </div>
      )}

      {/* 이벤트 상세 모달 */}
      {detailEvent && !showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setDetailEvent(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-3 h-3 rounded-sm mt-1 flex-shrink-0" style={{ backgroundColor: detailEvent._calendarColor || '#039BE5' }} />
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-100">{detailEvent.summary || '(제목 없음)'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{detailEvent._calendarSummary}</p>
              </div>
              <button onClick={() => setDetailEvent(null)} className="p-1 rounded hover:bg-gray-800 text-gray-500"><X size={15} /></button>
            </div>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex items-start gap-2 text-gray-400">
                <Clock size={14} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p>{detailEvent.startDate || detailEvent.start?.date || detailEvent.start?.dateTime?.slice(0, 10)}</p>
                  {formatEventTime(detailEvent) !== '종일' && <p className="text-xs">{formatEventTime(detailEvent)}</p>}
                  {formatEventTime(detailEvent) === '종일' && <p className="text-xs text-gray-500">종일</p>}
                </div>
              </div>
              {detailEvent.location && (
                <div className="flex items-start gap-2 text-gray-400">
                  <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                  <p>{detailEvent.location}</p>
                </div>
              )}
              {detailEvent.description && (
                <div className="flex items-start gap-2 text-gray-400">
                  <div className="w-3.5 flex-shrink-0" />
                  <p className="text-xs whitespace-pre-line">{detailEvent.description}</p>
                </div>
              )}
              {detailEvent.hangoutLink && (
                <a href={detailEvent.hangoutLink} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                  onClick={e => e.stopPropagation()}
                >
                  <Video size={14} className="flex-shrink-0" />
                  <span className="text-xs">Google Meet 참여</span>
                  <ExternalLink size={10} />
                </a>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-3 border-t border-gray-800">
              <button onClick={() => handleDelete(detailEvent)} disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-red-400 hover:bg-red-900/20 rounded-lg text-xs disabled:opacity-40 transition-colors">
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} 삭제
              </button>
              <button onClick={() => openEdit(detailEvent)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg text-xs transition-colors">
                <Pencil size={12} /> 수정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일정 추가/수정 모달 */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowForm(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-100">{isEditing ? '일정 수정' : '일정 추가'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-gray-800 text-gray-400"><X size={16} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="제목"
                autoFocus
                className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none focus:border-gray-500 placeholder-gray-500"
              />
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">캘린더</label>
                <div className="flex flex-wrap gap-1.5">
                  {calendars.map(cal => {
                    const selected = form.calendarId === cal.id;
                    return (
                      <button key={cal.id} type="button" onClick={() => setForm(f => ({ ...f, calendarId: cal.id }))}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-all"
                        style={{
                          backgroundColor: selected ? cal.backgroundColor : 'transparent',
                          borderColor: cal.backgroundColor,
                          color: selected ? autoTextColor(cal.backgroundColor) : cal.backgroundColor,
                        }}
                      >{cal.summary}</button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.allDay} onChange={e => setForm(f => ({ ...f, allDay: e.target.checked }))} className="accent-brand" />
                <span className="text-gray-400">종일</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">시작일</label>
                  <input type="date" value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value, endDate: f.endDate || e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">종료일</label>
                  <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none text-sm" />
                </div>
              </div>
              {!form.allDay && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">시작 시간</label>
                    <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                      className="w-full px-2 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">종료 시간</label>
                    <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                      className="w-full px-2 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none text-sm" />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">장소</label>
                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="장소 (선택)" className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none placeholder-gray-600" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">메모</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="메모 (선택)" rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none placeholder-gray-600 resize-none" />
              </div>
              {!form.allDay && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.meetLink} onChange={e => setForm(f => ({ ...f, meetLink: e.target.checked }))} className="accent-brand" />
                  <span className="text-gray-400 text-xs flex items-center gap-1"><Video size={12} /> Google Meet 링크 생성</span>
                </label>
              )}
              {isEditing && form.hangoutLink && (
                <a href={form.hangoutLink} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-blue-400 hover:underline">
                  <Video size={12} /> 기존 Meet 링크 열기
                </a>
              )}
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
              <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.startDate}
                className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-40 flex items-center gap-1.5">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                {isEditing ? '수정' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 캘린더 관리 모달 */}
      {showCatMgr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setShowCatMgr(false); setEditingCal(null); setCatForm(EMPTY_CAT_FORM); }}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-100">캘린더 관리</h3>
              <button onClick={() => { setShowCatMgr(false); setEditingCal(null); setCatForm(EMPTY_CAT_FORM); }} className="p-1 rounded hover:bg-gray-800 text-gray-400"><X size={16} /></button>
            </div>
            <div className="space-y-1.5 mb-4 max-h-48 overflow-y-auto">
              {calendars.map(cal => (
                <div key={cal.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${editingCal?.id === cal.id ? 'border-brand/40 bg-gray-800/50' : 'border-gray-800 hover:bg-gray-800/30'}`}>
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cal.backgroundColor }} />
                  <span className="text-sm text-gray-200 flex-1 truncate">{cal.summary}</span>
                  {cal.primary && <span className="text-[10px] text-gray-600 flex-shrink-0">기본</span>}
                  <button onClick={() => openEditCal(cal)} className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-gray-200 flex-shrink-0 transition-colors">
                    <Pencil size={12} />
                  </button>
                  {!cal.primary && (
                    <button onClick={() => handleDeleteCal(cal)} disabled={catDeleting === cal.id}
                      className="p-1 rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400 flex-shrink-0 transition-colors disabled:opacity-40">
                      {catDeleting === cal.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-gray-800 pt-4">
              <p className="text-xs text-gray-500 mb-2">{editingCal ? `'${editingCal.summary}' 수정` : '새 캘린더 추가'}</p>
              <input
                value={catForm.name}
                onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                placeholder="캘린더 이름"
                className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 focus:outline-none focus:border-gray-500 placeholder-gray-600 text-sm mb-3"
              />
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1.5">색상</p>
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_COLORS.map(({ bg, fg }) => (
                    <button
                      key={bg}
                      onClick={() => setCatForm(f => ({ ...f, backgroundColor: bg, foregroundColor: fg }))}
                      className="w-6 h-6 rounded-full transition-all"
                      style={{
                        backgroundColor: bg,
                        outline: catForm.backgroundColor === bg ? `2px solid ${bg}` : 'none',
                        outlineOffset: '2px',
                        transform: catForm.backgroundColor === bg ? 'scale(1.2)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                {editingCal && (
                  <button onClick={() => { setEditingCal(null); setCatForm(EMPTY_CAT_FORM); }}
                    className="px-3 py-1.5 text-gray-400 text-sm hover:text-white transition-colors">취소</button>
                )}
                <button onClick={handleSaveCal} disabled={catSaving || !catForm.name.trim()}
                  className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-40 flex items-center gap-1.5">
                  {catSaving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  {editingCal ? '수정' : '추가'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
